import { useMutation } from '@base-app/web2'
import { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CREATE_BANNER, UPDATE_BANNER } from '../../../graphql/banners/mutations'
import { BANNER_QUERY } from '../../../graphql/banners/queries'
import type { Banner } from '../../../graphql/banners/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { prependToLists } from '../../../lib/cacheLists'
import type { ImageDimensionRules } from '../../../lib/uploads'
import { lowResPathFor } from '../../../lib/uploads'
import { isWebUrl } from '../../../services/url-validation'
import { Button, FormActions } from '../../atoms'
import { FormField, FormLoadState, ImageUploadField, TextareaField } from '../../molecules'

// The mobile app lays banners out from an 800×301 base image
const BANNER_IMAGE_DIMENSIONS: ImageDimensionRules = { aspectRatio: { width: 800, height: 301 } }

interface BannerFormFieldsProps {
  banner?: Banner
  clientId: string
  appId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the banner loads
  skeleton?: boolean
}

export const BannerFormFields = memo(
  ({ banner, clientId, appId, onSuccess, onClose, skeleton }: BannerFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const isEdit = !!banner
    const [url, setUrl] = useState(banner?.url ?? '')
    const [image, setImage] = useState(banner?.image ?? '')
    const [uploadingImage, setUploadingImage] = useState(false)
    const [position, setPosition] = useState(banner?.position ?? 0)
    const [observations, setObservations] = useState(banner?.observations ?? '')
    const [urlError, setUrlError] = useState<string | undefined>()
    const [imageError, setImageError] = useState<string | undefined>()
    const [positionError, setPositionError] = useState<string | undefined>()

    const makeOptimistic = useCallback(
      (id: string): Banner => ({
        __typename: 'Banner',
        id,
        client_id: clientId,
        application_id: appId,
        url: url || null,
        image: image || null,
        image_low_res: lowResPathFor(image),
        position: Number(position) || 0,
        observations: observations || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
      [clientId, appId, url, image, position, observations],
    )

    const [createBanner, { loading: creating }] = useMutation(CREATE_BANNER, {
      optimisticResponse: { createBanner: makeOptimistic(`temp-${Date.now()}`) },
      update(cache, result) {
        const item = result.data?.createBanner
        if (!item) {
          return
        }
        prependToLists(cache, 'banners', item, { clientId, applicationId: appId })
      },
      onError(err) {
        showToast(err.message, 'error')
      },
      onCompleted() {
        onSuccess()
      },
    })

    const [updateBanner, { loading: updating }] = useMutation(UPDATE_BANNER, {
      optimisticResponse: { updateBanner: makeOptimistic(banner?.id ?? '') },
      onError(err) {
        showToast(err.message, 'error')
      },
      onCompleted() {
        onSuccess()
      },
    })

    const loading = creating || updating || uploadingImage

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        const nextUrlError = isWebUrl(url.trim()) ? undefined : t('banners.form.urlInvalid')
        // A banner is its image: without one the app has nothing to show
        const nextImageError = image ? undefined : t('banners.form.imageRequired')
        const nextPositionError =
          Number.isInteger(position) && position >= 0
            ? undefined
            : t('banners.form.positionInvalid')
        setUrlError(nextUrlError)
        setImageError(nextImageError)
        setPositionError(nextPositionError)
        if (nextUrlError || nextImageError || nextPositionError) {
          return
        }
        const vars = {
          url: url.trim() || null,
          image: image || null,
          position: Number(position) || 0,
          observations: observations || null,
        }
        if (isEdit) {
          updateBanner({ variables: { id: banner.id, ...vars } })
        } else {
          createBanner({ variables: { client_id: clientId, application_id: appId, ...vars } })
        }
      },
      [
        t,
        isEdit,
        banner?.id,
        url,
        image,
        position,
        observations,
        clientId,
        appId,
        updateBanner,
        createBanner,
      ],
    )

    return (
      <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
        {skeleton ? (
          <p className="sr-only" role="status">
            {t('common.loading')}
          </p>
        ) : null}
        <FormField
          id="banner-url"
          label={t('banners.form.urlLabel')}
          name="url"
          value={url}
          onChange={e => setUrl(e.target.value)}
          error={urlError}
          placeholder="https://"
          skeleton={skeleton}
        />
        <FormField
          id="banner-position"
          label={t('banners.form.positionLabel')}
          name="position"
          type="number"
          value={position}
          onChange={e => setPosition(Number(e.target.value))}
          error={positionError}
          min={0}
          step={1}
          skeleton={skeleton}
        />
        <ImageUploadField
          applicationId={appId}
          id="banner-image-file"
          target="banners"
          dimensions={BANNER_IMAGE_DIMENSIONS}
          label={t('banners.form.imageLabel')}
          value={image}
          onChange={setImage}
          onUploadingChange={setUploadingImage}
          error={imageError}
          skeleton={skeleton}
        />
        <TextareaField
          id="banner-observations"
          label={t('banners.form.observationsLabel')}
          name="observations"
          value={observations}
          onChange={e => setObservations(e.target.value)}
          skeleton={skeleton}
        />
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="submit" disabled={loading} skeleton={skeleton}>
            {loading ? t('banners.form.saving') : t('banners.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

BannerFormFields.displayName = 'BannerFormFields'

interface BannerFormProps extends Omit<BannerFormFieldsProps, 'banner'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const BannerForm = memo(({ id, ...props }: BannerFormProps) => {
  const { record, status } = useRecordById(BANNER_QUERY, id, data => data.banner)
  if (status === 'loading') {
    return <BannerFormFields skeleton {...props} />
  }
  if (status !== 'new' && status !== 'ready') {
    return <FormLoadState status={status} onClose={props.onClose} />
  }
  return <BannerFormFields key={id ?? 'new'} banner={record} {...props} />
})

BannerForm.displayName = 'BannerForm'
